import React, { useEffect, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import {
    Box, Card, CardContent, Switch, Table, TableBody, TableCell, TableContainer, TableHead,
    TableRow, Typography,
} from "@mui/material";
import { cardTitleSx, numberSx, tokens } from "../../theme";
import { roundToX } from "../../common/common";
import {
    setNrsEnabled, setNrsIterations, setNrsMultiplier, setNrsKeepBestPercent,
} from "../../reducer/RawSurfaceSlice";
import MiniField from "../cards/MiniField";

// Same table shape as Scan Analysis's own NoiseReductionScatterCard.js (src/components/cards/),
// reusing its column set (Initial/Kept/Passes/Std Y/Std Z) - different wiring only (this commits
// live via RawSurfaceSlice's applyRawSurfaceFilters, not CalcSettingsSlice's dirty/Apply pattern).
const headerCellSx = {
    fontSize: "10.5px", fontWeight: 600, color: tokens.muted,
    textTransform: "uppercase", letterSpacing: "0.04em", border: "none", padding: "3px 4px",
    whiteSpace: "nowrap",
};
const rowHeaderCellSx = {
    fontSize: "12px", fontWeight: 600, color: tokens.textSoft, border: "none", padding: "3px 4px",
    whiteSpace: "nowrap",
};
const numCellSx = { ...numberSx, fontSize: "12px", border: "none", padding: "3px 4px" };
const capitalize = (s) => s.charAt(0).toUpperCase() + s.slice(1);

export default function NoiseReductionScatterCard({ commit }) {
    const rawSurface = useSelector((state) => state.rawSurface);
    const dispatch = useDispatch();

    // Local text state, commit-on-blur - same convention as the Shaft Ranges/Circle Fit fields in
    // RawSurfaceSettings.js, not CalcSettingsSlice's per-keystroke-to-local-state pattern.
    const [iterationsText, setIterationsText] = useState(String(rawSurface.nrsIterations));
    const [multiplierText, setMultiplierText] = useState(String(rawSurface.nrsMultiplier));
    const [keepBestText, setKeepBestText] = useState(String(Math.round(rawSurface.nrsKeepBestPercent * 100)));
    const [iterationError, setIterationError] = useState(false);
    const [multiplierError, setMultiplierError] = useState(false);
    const [keepBestError, setKeepBestError] = useState(false);

    useEffect(() => setIterationsText(String(rawSurface.nrsIterations)), [rawSurface.nrsIterations]);
    useEffect(() => setMultiplierText(String(rawSurface.nrsMultiplier)), [rawSurface.nrsMultiplier]);
    useEffect(
        () => setKeepBestText(String(Math.round(rawSurface.nrsKeepBestPercent * 100))),
        [rawSurface.nrsKeepBestPercent]
    );

    const commitIterations = () => {
        const error = iterationsText.length === 0 || Number(iterationsText) === 0;
        setIterationError(error);
        if (error) return;
        dispatch(setNrsIterations({ nrsIterations: Number(iterationsText) }));
        commit();
    };
    const commitMultiplier = () => {
        const error = multiplierText.length === 0 || Number(multiplierText) === 0;
        setMultiplierError(error);
        if (error) return;
        dispatch(setNrsMultiplier({ nrsMultiplier: Number(multiplierText) }));
        commit();
    };
    const commitKeepBest = () => {
        const error = keepBestText.length === 0 || Number(keepBestText) === 0;
        setKeepBestError(error);
        if (error) return;
        dispatch(setNrsKeepBestPercent({ nrsKeepBestPercent: Number(keepBestText) / 100 }));
        commit();
    };

    const renderTraceTable = () => {
        if (!rawSurface.filterTrace) return null;
        const rows = ["machine", "motor"]
            .map((dataType) => ({
                dataType,
                filter: rawSurface.filterTrace.filters.find(
                    (f) => f.type === "NoiseReductionScatter" && f.data === dataType
                ),
            }))
            .filter((row) => row.filter);

        if (rows.length === 0) return null;

        return (
            <TableContainer>
                <Table size="small" sx={{ mt: 1 }}>
                    <TableHead>
                        <TableRow>
                            <TableCell sx={headerCellSx} />
                            <TableCell align="right" sx={headerCellSx}>Initial</TableCell>
                            <TableCell align="right" sx={headerCellSx}>Kept</TableCell>
                            <TableCell align="right" sx={headerCellSx}>Passes</TableCell>
                            <TableCell align="right" sx={headerCellSx}>Std Y</TableCell>
                            <TableCell align="right" sx={headerCellSx}>Std Z</TableCell>
                        </TableRow>
                    </TableHead>
                    <TableBody>
                        {rows.map(({ dataType, filter }) => (
                            <TableRow key={dataType}>
                                <TableCell component="th" scope="row" sx={rowHeaderCellSx}>
                                    {capitalize(dataType)}
                                </TableCell>
                                <TableCell align="right" sx={numCellSx}>{filter.trace.initialPointCount}</TableCell>
                                <TableCell align="right" sx={numCellSx}>{filter.trace.keptPointCount}</TableCell>
                                <TableCell align="right" sx={numCellSx}>{filter.trace.iterations}</TableCell>
                                <TableCell align="right" sx={numCellSx}>{roundToX(filter.trace.stdY, 1)}</TableCell>
                                <TableCell align="right" sx={numCellSx}>{roundToX(filter.trace.stdZ, 1)}</TableCell>
                            </TableRow>
                        ))}
                    </TableBody>
                </Table>
            </TableContainer>
        );
    };

    return (
        <Card>
            <CardContent>
                <Typography sx={cardTitleSx}>Noise Reduction Scatter</Typography>
                <Typography variant="caption" color="text.secondary">
                    Drops points beyond k &times; that axis&apos;s own residual scatter, per
                    machine/motor segment.
                </Typography>

                <Box sx={{ display: "flex", justifyContent: "space-between", alignItems: "center", mt: 1.5, mb: 1 }}>
                    <Typography variant="body2">Enabled</Typography>
                    <Switch
                        size="small"
                        checked={rawSurface.nrsEnabled}
                        onChange={(e) => {
                            dispatch(setNrsEnabled({ enabled: e.target.checked }));
                            commit();
                        }}
                    />
                </Box>

                {rawSurface.nrsEnabled && (
                    <>
                        <Box sx={{ display: "flex", gap: "8px", mb: "10px" }}>
                            <MiniField
                                label="Iterations" value={iterationsText}
                                onChange={(e) => setIterationsText(e.target.value)}
                                onBlur={commitIterations}
                                maxLength={3} error={iterationError}
                            />
                            <MiniField
                                label="Multiplier (k)" value={multiplierText}
                                onChange={(e) => setMultiplierText(e.target.value)}
                                onBlur={commitMultiplier}
                                maxLength={4} error={multiplierError}
                            />
                            <MiniField
                                label="Keep best %" value={keepBestText}
                                onChange={(e) => setKeepBestText(e.target.value)}
                                onBlur={commitKeepBest}
                                maxLength={2} error={keepBestError}
                            />
                        </Box>
                        {renderTraceTable()}
                    </>
                )}
            </CardContent>
        </Card>
    );
}
